import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MyAELayout } from "../components/myae/Layout";
import { useAuth } from "../context/AuthContext";
import { listAnalyses, fetchProfile, getAnalysis, deleteAnalysis } from "../lib/api";
import { toast } from "sonner";
import {
  FilePlus2, FileText, TrendingUp, GraduationCap, Gauge, FolderOpen,
  ArrowRight, MoreHorizontal, Copy, Trash2, Loader2, Award, Search,
} from "lucide-react";
import HelpImproveMyaeCard from "../components/community/HelpImproveMyaeCard";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatusPill = ({ status, locked }) => {
  const map = {
    complete: locked ? ["bg-amber-100 text-amber-800", "Feedback only"] : ["bg-emerald-100 text-emerald-800", "Complete"],
    processing: ["bg-indigo-100 text-indigo-700", "Processing"],
    error: ["bg-red-100 text-red-800", "Failed"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-600", status];
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
};

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-2 text-zinc-400 mb-2">
      <Icon size={16} /> <span className="text-xs font-semibold tracking-wide uppercase">{label}</span>
    </div>
    <p className="font-display text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [profile, setProfile] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = () => listAnalyses().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    refresh();
    if (user) fetchProfile().then(setProfile).catch(() => {});
  }, [user]);

  const completed = (items || []).filter((a) => a.status === "complete");
  const grades = completed.map((a) => a.report?.estimated_grade?.percentage).filter((g) => typeof g === "number");
  const avg = (xs) => (xs.length ? Math.round(xs.reduce((s, x) => s + x, 0) / xs.length) : 0);

  const stats = {
    analysed: completed.length,
    avgGrade: profile?.average_grade ?? avg(grades),
    avgConfidence: profile?.average_confidence,
    documents: profile?.documents_uploaded ?? (items || []).length,
    improvement: profile?.total_improvement,
  };

  const openReport = (id) => navigate(`/report/${id}`);

  const duplicate = async (id) => {
    setBusyId(id); setMenuId(null);
    try {
      const doc = await getAnalysis(id);
      navigate("/new", { state: { autostart: {
        text: doc.text, filename: doc.filename, title: doc.title, question: doc.question,
        outcomes: doc.outcomes, rubric: doc.rubric, assignment_guide: doc.assignment_guide || "",
        supporting_docs: doc.supporting_docs || [], extraction: doc.extraction || null,
      } } });
    } catch (e) { toast.error("Could not duplicate this analysis."); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setMenuId(null);
    try { await deleteAnalysis(id); toast.success("Analysis deleted."); refresh(); }
    catch (e) { toast.error("Could not delete."); }
  };

  return (
    <MyAELayout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900">
              {user ? `Welcome back${user.name ? `, ${user.name.split(" ")[0]}` : ""}` : "Your dashboard"}
            </h1>
            <p className="text-zinc-500 mt-1">Track your assignments and see your predicted grades before you submit.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/community")} data-testid="community-link"
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm font-medium text-zinc-700 hover:border-indigo-500 hover:text-indigo-600 transition-colors">
              <Award size={16} /> Community
            </button>
            <button onClick={() => navigate("/new")} data-testid="dashboard-new-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition-all active:scale-[0.98]">
              <FilePlus2 size={17} /> New Analysis
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="dashboard-stats">
          <StatCard icon={FileText} label="Analysed" value={stats.analysed} sub="assignments" testId="stat-analysed" />
          <StatCard icon={GraduationCap} label="Avg grade" value={stats.avgGrade ? `${stats.avgGrade}%` : "—"} sub="predicted" testId="stat-avg-grade" />
          <StatCard icon={Gauge} label="Avg confidence" value={stats.avgConfidence != null ? `${stats.avgConfidence}%` : "—"} sub="reliability" testId="stat-avg-confidence" />
          <StatCard icon={FolderOpen} label="Documents" value={stats.documents} sub="uploaded" testId="stat-documents" />
          <StatCard icon={TrendingUp} label="Improvement" value={stats.improvement != null ? `${stats.improvement > 0 ? "+" : ""}${stats.improvement}` : "—"} sub="since first draft" testId="stat-improvement" />
        </div>

        {/* New analysis prompt card */}
        <button onClick={() => navigate("/new")} data-testid="new-analysis-card"
          className="w-full rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 hover:bg-indigo-50 transition-colors p-8 flex flex-col items-center justify-center text-center mb-10 group">
          <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-[0_8px_24px_rgba(79,70,229,0.35)] group-hover:scale-105 transition-transform">
            <FilePlus2 size={26} className="text-white" />
          </div>
          <p className="font-display text-xl font-medium text-zinc-900 mt-4">Start a new analysis</p>
          <p className="text-sm text-zinc-500 mt-1 max-w-md">Upload your assignment brief, supporting documents and essay — get examiner feedback in about a minute.</p>
        </button>

        {/* Community Training Programme card — voluntary Turnitin contribution */}
        <div className="mb-8"><HelpImproveMyaeCard /></div>

        {/* Recent analyses */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-medium text-zinc-900">Recent analyses</h2>
          {(items || []).length > 6 && <button onClick={() => navigate("/reports")} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1">View all <ArrowRight size={14} /></button>}
        </div>

        {items === null ? (
          <div className="flex items-center gap-2 text-zinc-500 py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-12 text-center" data-testid="dashboard-empty">
            <FileText size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="font-medium text-zinc-900">No analyses yet</p>
            <p className="text-sm text-zinc-500 mt-1">Run your first analysis to see it here.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recent-analyses">
            {items.slice(0, 6).map((a) => {
              const g = a.report?.estimated_grade?.percentage;
              const conf = a.report?.prediction_confidence;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all relative" data-testid={`analysis-card-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => a.status === "complete" && openReport(a.id)}>
                      <p className="font-medium text-zinc-900 truncate" title={a.title || a.question || a.filename}>{a.title || a.question || a.filename || "Untitled"}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{fmtDate(a.created_at)}</p>
                    </div>
                    <button onClick={() => setMenuId(menuId === a.id ? null : a.id)} data-testid={`card-menu-${a.id}`} className="text-zinc-400 hover:text-zinc-700 shrink-0 p-1">
                      {busyId === a.id ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={18} />}
                    </button>
                  </div>

                  {menuId === a.id && (
                    <div className="absolute right-4 top-12 z-20 w-52 rounded-xl border border-zinc-200 bg-white shadow-lg py-1" data-testid={`card-actions-${a.id}`}>
                      {a.status === "complete" && <button onClick={() => openReport(a.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"><FileText size={15} /> Open Report</button>}
                      {a.status === "complete" && <button onClick={() => navigate(`/report/${a.id}#si-panel-wrapper`)} data-testid={`card-si-${a.id}`} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 font-medium"><Search size={15} /> Research & Check</button>}
                      <button onClick={() => duplicate(a.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"><Copy size={15} /> Duplicate</button>
                      <button onClick={() => remove(a.id)} data-testid={`card-delete-${a.id}`} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 size={15} /> Delete</button>
                    </div>
                  )}

                  <div className="mt-4 flex items-end justify-between cursor-pointer" onClick={() => a.status === "complete" && openReport(a.id)}>
                    <div>
                      <p className="overline text-zinc-400">Predicted</p>
                      <p className="font-display text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums leading-none mt-0.5">
                        {a.report?.grade_locked ? "—" : (typeof g === "number" ? `${g}%` : "—")}
                      </p>
                    </div>
                    <div className="text-right">
                      {typeof conf === "number" && <p className="text-xs text-zinc-500">Confidence <span className="font-medium text-zinc-700">{conf}%</span></p>}
                      <div className="mt-1.5"><StatusPill status={a.status} locked={a.report?.grade_locked} /></div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </MyAELayout>
  );
}
